/**
 * useSystemEvents Hook
 *
 * Custom hook for handling system-related WebSocket events.
 * Manages state refresh, data refresh, door notifications, and subscription confirmations.
 *
 * Phase 3C: System Events Extraction (PR #3)
 *
 * Events handled:
 * - stateRefreshed: Re-fetch device states (without credentials)
 * - dataRefreshed: Full data sync (devices + credentials)
 * - doorNotification: Door status notifications (toast notifications)
 * - subscribeConfirmation: SNS subscription confirmation for WebSocket events
 *
 * @module hooks/useSystemEvents
 */

import { useCallback } from "react";
import {
  useDevicesStore,
  useCredentialsStore,
  useBulkOperationsStore,
} from "../stores";
import logger from "../utils/logger";

/**
 * Custom hook for handling system-related WebSocket events
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.installationId - Current installation ID
 * @param {Object} params.selectedDeviceIdRef - Ref to currently selected device ID
 * @param {Object} params.deviceStates - Current device states object
 * @param {Array} params.devices - Current devices array
 * @param {Function} params.fetchDevices - Function to fetch all devices
 * @param {Function} params.fetchDeviceStateReadOnly - Function to fetch device state (read-only)
 * @param {Function} params.invalidateDeviceStateCache - Function to invalidate device cache
 * @param {Function} params.setInstallationSyncCache - Function to set installation sync cache
 * @param {Function} params.setSyncing - Function to set syncing state
 * @param {Function} params.updateMultipleDeviceStates - Function to update multiple device states
 * @param {Function} params.setActionHistory - Function to update action history
 * @param {Function} params.showNotification - Function to show notifications
 * @param {number} params.MAX_HISTORY_IN_MEMORY - Maximum number of actions to keep in history
 * @param {Function} params.shouldAllowFullSync - Function to check if full sync is allowed (debounce)
 * @param {Function} params.getStatusStore - Function to get status store from localStorage
 * @param {Object} params.orgInstallContext - Organization/installation context
 *
 * @returns {Object} Object with system event handler functions
 * @returns {Function} handleStateRefreshed - Handler for stateRefreshed events
 * @returns {Function} handleDataRefreshed - Handler for dataRefreshed events
 * @returns {Function} handleDoorNotification - Handler for doorNotification events
 * @returns {Function} handleSubscribeConfirmation - Handler for subscribeConfirmation events
 */
export const useSystemEvents = ({
  installationId,
  selectedDeviceIdRef,
  deviceStates,
  devices,
  fetchDevices,
  fetchDeviceStateReadOnly,
  invalidateDeviceStateCache,
  setInstallationSyncCache,
  setSyncing,
  updateMultipleDeviceStates,
  setActionHistory,
  showNotification,
  MAX_HISTORY_IN_MEMORY,
  shouldAllowFullSync,
  getStatusStore,
  orgInstallContext,
}) => {
  // Access Zustand stores
  const { setDevices, setDeviceStates, setSelectedDeviceId } =
    useDevicesStore();

  const { setCredentials } = useCredentialsStore();

  // Bulk operations state (Phase 1 Days 43-45)
  const { setBulkDeviceLockdownStates } = useBulkOperationsStore();

  /**
   * Handler for stateRefreshed events
   *
   * Re-fetches device states without credentials (state-only sync).
   * Only updates lockdown states and other device state fields.
   *
   * @param {Object} message - WebSocket message
   */
  const handleStateRefreshed = useCallback(
    (message) => {
      // State-only refresh (no credentials) - fetch states without credentials
      if (message.deviceId === "all") {
        logger.log("[WebSocket] stateRefreshed:all received (state-only sync)");

        showNotification({
          message: "Device states synchronized.",
          type: "success",
        });

        // Fetch device list and states (without credentials)
        logger.log(
          "[stateRefreshed:all] Fetching states for all devices (no credentials)..."
        );
        fetchDevices()
          .then((allDevices) => {
            const deviceList = allDevices.filter(
              (device) => device.installationId === installationId
            );

            if (deviceList && deviceList.length > 0) {
              logger.log(
                `[stateRefreshed:all] Fetching state for ${deviceList.length} devices...`
              );

              // Fetch state for ALL devices in parallel (no credentials)
              const statePromises = deviceList.map((device) =>
                fetchDeviceStateReadOnly(device.deviceId)
                  .then((state) => ({ deviceId: device.deviceId, state }))
                  .catch((error) => {
                    logger.error(
                      `Failed to fetch state for device ${device.deviceId}:`,
                      error
                    );
                    return { deviceId: device.deviceId, state: null };
                  })
              );

              return Promise.all(statePromises);
            }
            return [];
          })
          .then((deviceStatesArray) => {
            if (deviceStatesArray.length > 0) {
              // Update deviceStates with lockdown states only
              const updates = deviceStatesArray
                .filter(({ state }) => state !== null)
                .map(({ deviceId, state }) => ({
                  deviceId,
                  state: {
                    ...deviceStates[deviceId],
                    ...state,
                    lastUpdated: Date.now(),
                  },
                }));

              updates.forEach(({ deviceId, state }) => {
                logger.log(
                  `[stateRefreshed:all] Updated state for ${deviceId} (lockdownState: ${state.lockdownState})`
                );
              });

              updateMultipleDeviceStates(updates);

              // Mark cache as valid
              setInstallationSyncCache(installationId);
              logger.log(
                "[stateRefreshed:all] ✅ All device states updated without credentials"
              );
            }
          })
          .catch((error) => {
            logger.error(
              "[stateRefreshed:all] Failed to fetch device states:",
              error
            );
          });
      }
    },
    [
      installationId,
      deviceStates,
      fetchDevices,
      fetchDeviceStateReadOnly,
      updateMultipleDeviceStates,
      setInstallationSyncCache,
      showNotification,
    ]
  );

  /**
   * Handler for dataRefreshed events
   *
   * Full data sync including devices, credentials, and states.
   * Handles device list updates, orphaned device cleanup, and auto-selection logic.
   *
   * @param {Object} message - WebSocket message
   */
  const handleDataRefreshed = useCallback(
    (message) => {
      const currentDeviceId = selectedDeviceIdRef.current;

      // Show notification for all sync events
      showNotification({
        message: `Background data sync complete for ${
          message.deviceId === "all" ? "all devices" : "current device"
        }.`,
        type: "success",
      });

      if (message.deviceId === "all") {
        // Use module-level debounce to prevent duplicate syncs
        if (!shouldAllowFullSync()) {
          logger.log(
            "[dataRefreshed:all] ⏭️ Skipping duplicate full sync (debounced)"
          );
          return; // Skip duplicate sync - DON'T set isSyncing!
        }

        // Only set syncing state if we're actually going to process the sync
        setSyncing(true);

        // Invalidate all device caches on full sync
        try {
          const keys = Object.keys(sessionStorage);
          keys.forEach((key) => {
            if (key.startsWith("device_state_")) {
              sessionStorage.removeItem(key);
            }
          });
          logger.log("Invalidated all device state caches on full sync");
        } catch (error) {
          logger.warn("Failed to invalidate all caches:", error);
        }
      } else if (message.deviceId === currentDeviceId) {
        // Only set syncing for current device sync
        setSyncing(true);
        invalidateDeviceStateCache(message.deviceId);
      } else {
        // Invalidate cache for other devices without setting syncing state
        invalidateDeviceStateCache(message.deviceId);
      }

      if (message.deviceId === "all") {
        logger.log("Full sync complete. Fetching state for ALL devices...");

        // Fetch device list first
        fetchDevices()
          .then((allDevices) => {
            const deviceList = allDevices.filter(
              (device) => device.installationId === installationId
            );

            if (deviceList && deviceList.length > 0) {
              // Sort devices consistently
              deviceList.sort((a, b) => {
                const nameA = a.displayName || a.serialNumber || a.deviceId;
                const nameB = b.displayName || b.serialNumber || b.deviceId;
                return nameA.localeCompare(nameB);
              });

              // Detect newly added devices for auto-selection
              const previousDeviceIds = new Set(
                Array.isArray(devices) ? devices.map((d) => d.deviceId) : []
              );
              const newDevices = deviceList.filter(
                (device) => !previousDeviceIds.has(device.deviceId)
              );
              const hasNewDevices = newDevices.length > 0;
              const isFirstDevice =
                previousDeviceIds.size === 0 && deviceList.length > 0;

              setDevices(deviceList);
              logger.log(
                `[dataRefreshed:all] Fetching state for ${deviceList.length} devices...`
              );

              // Create Set of current device IDs for fast lookup
              const currentDeviceIds = new Set(
                deviceList.map((d) => d.deviceId)
              );

              // Clean up state for devices no longer in list
              setDeviceStates((prevStates) => {
                const orphanedDeviceIds = Object.keys(prevStates).filter(
                  (deviceId) => !currentDeviceIds.has(deviceId)
                );
                if (orphanedDeviceIds.length > 0) {
                  const cleanedStates = { ...prevStates };
                  orphanedDeviceIds.forEach((deviceId) => {
                    delete cleanedStates[deviceId];
                    logger.log(
                      `[dataRefreshed:all] 🧹 Removed orphaned deviceState for ${deviceId}`
                    );
                  });
                  return cleanedStates;
                }
                return prevStates;
              });

              setBulkDeviceLockdownStates((prevStates) => {
                const orphanedDeviceIds = Object.keys(prevStates).filter(
                  (deviceId) => !currentDeviceIds.has(deviceId)
                );
                if (orphanedDeviceIds.length > 0) {
                  const cleanedStates = { ...prevStates };
                  orphanedDeviceIds.forEach((deviceId) => {
                    delete cleanedStates[deviceId];
                    logger.log(
                      `[dataRefreshed:all] 🧹 Removed orphaned bulkDeviceLockdownState for ${deviceId}`
                    );
                  });
                  return cleanedStates;
                }
                return prevStates;
              });

              // Validate selectedDeviceId and auto-select new devices
              setSelectedDeviceId((currentSelectedId) => {
                // Check if current selection is still valid
                if (
                  currentSelectedId &&
                  !currentDeviceIds.has(currentSelectedId)
                ) {
                  logger.log(
                    `[dataRefreshed:all] ⚠️ Selected device ${currentSelectedId} no longer exists, auto-selecting first device`
                  );
                  const newSelection = deviceList[0]?.deviceId || null;
                  if (newSelection) {
                    logger.log(
                      `[dataRefreshed:all] ✅ Auto-selected device: ${newSelection}`
                    );
                  } else {
                    logger.log(
                      `[dataRefreshed:all] ℹ️ No devices available, cleared selection`
                    );
                  }
                  return newSelection;
                }

                // Auto-select newly claimed devices
                if (!currentSelectedId && hasNewDevices) {
                  const newDeviceId = newDevices[0].deviceId;
                  logger.log(
                    `[dataRefreshed:all] 🆕 No selection, auto-selecting newly claimed device: ${newDeviceId}`
                  );
                  return newDeviceId;
                }

                if (!currentSelectedId && isFirstDevice) {
                  const firstDeviceId = deviceList[0].deviceId;
                  logger.log(
                    `[dataRefreshed:all] 🎉 First device claimed, auto-selecting: ${firstDeviceId}`
                  );
                  return firstDeviceId;
                }

                return currentSelectedId;
              });

              // Fetch state for ALL devices in parallel
              const statePromises = deviceList.map((device) =>
                fetchDeviceStateReadOnly(device.deviceId)
                  .then((state) => ({ deviceId: device.deviceId, state }))
                  .catch((error) => {
                    logger.error(
                      `Failed to fetch state for device ${device.deviceId}:`,
                      error
                    );
                    return { deviceId: device.deviceId, state: null };
                  })
              );

              return Promise.all(statePromises);
            }
            return [];
          })
          .then((deviceStatesArray) => {
            if (deviceStatesArray.length > 0) {
              // Update deviceStates for all devices
              setDeviceStates((prevStates) => {
                const updatedStates = { ...prevStates };
                let hasChanges = false;

                deviceStatesArray.forEach(({ deviceId, state }) => {
                  const prevState = prevStates[deviceId] || {};

                  // Initialize defaults for new devices even if fetch failed
                  if (!prevStates[deviceId]) {
                    const defaultState = {
                      connectivity: {
                        lastSeen: null,
                        signalStrength: 0,
                        batteryLevel: 0,
                      },
                    };
                    updatedStates[deviceId] = state || defaultState;
                    hasChanges = true;
                    logger.log(
                      `[dataRefreshed:all] 🆕 Initialized state for new device ${deviceId}`
                    );
                  } else if (state) {
                    // Existing device with successful fetch
                    const newState = { ...prevState, ...state };

                    // Only update if state has actually changed
                    const stateChanged =
                      JSON.stringify(prevState) !== JSON.stringify(newState);
                    if (stateChanged) {
                      updatedStates[deviceId] = newState;
                      hasChanges = true;
                      logger.log(
                        `[dataRefreshed:all] Device ${deviceId}: lockdownState="${state.lockdownState}", lockState="${state.lockState}" (CHANGED)`
                      );
                    }
                  }
                });

                logger.log(
                  `[dataRefreshed:all] ✅ ${
                    hasChanges ? "Updated" : "No changes to"
                  } deviceStates for ${
                    deviceStatesArray.filter((d) => d.state).length
                  } devices`
                );
                return hasChanges ? updatedStates : prevStates;
              });

              // Update bulk lockdown states
              setBulkDeviceLockdownStates((prevStates) => {
                const updatedLockdownStates = { ...prevStates };
                let devicesWithLockdownState = 0;
                let hasChanges = false;

                deviceStatesArray.forEach(({ deviceId, state }) => {
                  if (state) {
                    logger.log(
                      `[dataRefreshed:all] 🔍 Device ${deviceId} state:`,
                      {
                        lockdownState: state.lockdownState,
                        hasLockdownState: "lockdownState" in state,
                      }
                    );
                    if (state.lockdownState) {
                      if (prevStates[deviceId] !== state.lockdownState) {
                        updatedLockdownStates[deviceId] = state.lockdownState;
                        hasChanges = true;
                        logger.log(
                          `[dataRefreshed:all] 🔒 Updated bulk lockdown state for ${deviceId}: ${state.lockdownState} (CHANGED)`
                        );
                      }
                      devicesWithLockdownState++;
                    }
                  }
                });

                logger.log(
                  `[dataRefreshed:all] ✅ ${
                    hasChanges ? "Updated" : "No changes to"
                  } bulkDeviceLockdownStates for ${devicesWithLockdownState} devices (total tracked: ${
                    Object.keys(updatedLockdownStates).length
                  })`
                );
                logger.log(
                  `[dataRefreshed:all] 📋 Final bulkDeviceLockdownStates:`,
                  updatedLockdownStates
                );
                return hasChanges ? updatedLockdownStates : prevStates;
              });

              // Update installation sync cache timestamp
              logger.log(
                `[dataRefreshed:all] ✅ Marking installation sync cache as valid`
              );
              setInstallationSyncCache(installationId);
            }
          })
          .catch((error) => {
            logger.error("Error re-fetching devices after refresh:", error);
            showNotification({
              message: "Error updating device list.",
              type: "error",
            });
          });
      }

      // Fetch current device state (using currentDeviceId from above)
      logger.log(
        "Cache refreshed. Re-fetching current device state via READ-ONLY..."
      );
      fetchDeviceStateReadOnly(currentDeviceId)
        .then((data) => {
          if (data && data.deviceId === currentDeviceId) {
            logger.log("Fetched device state (read-only):", data);
            logger.log("Credentials from sync:", data.credentials);

            const statusStore = getStatusStore();
            const mergedCreds = (data.credentials || []).map((cred) => ({
              ...cred,
              status: cred.status || statusStore[cred.id] || "Active",
            }));
            logger.log("Merged credentials:", mergedCreds);

            setDeviceStates((prevStates) => {
              const prevDeviceState = prevStates[data.deviceId] || {};
              const newDeviceState = { ...prevDeviceState, ...data };

              const stateChanged =
                JSON.stringify(prevDeviceState) !==
                JSON.stringify(newDeviceState);
              if (!stateChanged) {
                logger.log(
                  `[dataRefreshed] No state changes detected for device ${data.deviceId}`
                );
                return prevStates;
              }

              logger.log(
                `[dataRefreshed] State changed for device ${data.deviceId}`
              );
              return {
                ...prevStates,
                [data.deviceId]: newDeviceState,
              };
            });

            setCredentials((prevCreds) => {
              const credsChanged =
                JSON.stringify(prevCreds) !== JSON.stringify(mergedCreds);
              if (!credsChanged) {
                logger.log(`[dataRefreshed] No credential changes detected`);
                return prevCreds;
              }
              logger.log(`[dataRefreshed] Credentials changed, updating`);
              return mergedCreds;
            });

            logger.log(
              `[dataRefreshed:all] ✅ Installation-wide sync complete`
            );

            setActionHistory((prevHistory) => {
              if (data.lastAction) {
                const currentLatest = prevHistory[0];
                if (
                  !currentLatest ||
                  !currentLatest.createdAt ||
                  (data.lastAction.createdAt &&
                    new Date(data.lastAction.createdAt) >=
                      new Date(currentLatest.createdAt))
                ) {
                  const exists = prevHistory.some(
                    (a) => a.actionId === data.lastAction.actionId
                  );
                  if (!exists) {
                    return [data.lastAction, ...prevHistory].slice(
                      0,
                      MAX_HISTORY_IN_MEMORY
                    );
                  } else {
                    const existingIndex = prevHistory.findIndex(
                      (a) => a.actionId === data.lastAction.actionId
                    );
                    if (
                      existingIndex !== -1 &&
                      !prevHistory[existingIndex].lastUpdatedAt &&
                      data.lastAction.lastUpdatedAt
                    ) {
                      const newHistory = [...prevHistory];
                      newHistory[existingIndex] = data.lastAction;
                      return newHistory;
                    }
                  }
                }
              }
              return prevHistory;
            });

            if ("displayName" in data) {
              setDevices((prevDevices) => {
                if (!Array.isArray(prevDevices)) {
                  logger.error(
                    "[dataRefreshed] prevDevices is not an array, skipping displayName update"
                  );
                  return prevDevices;
                }
                let needsSort = false;
                const updated = prevDevices.map((dev) => {
                  if (
                    dev.deviceId === data.deviceId &&
                    dev.displayName !== data.displayName
                  ) {
                    needsSort = true;
                    return { ...dev, displayName: data.displayName };
                  }
                  return dev;
                });

                if (needsSort) {
                  return updated.sort((a, b) =>
                    (
                      a.displayName ||
                      a.serialNumber ||
                      a.deviceId
                    ).localeCompare(
                      b.displayName || b.serialNumber || b.deviceId
                    )
                  );
                }
                return prevDevices;
              });
            }
          }
        })
        .catch((error) => {
          logger.error(
            "[dataRefreshed] Failed to fetch current device state:",
            error
          );
        })
        .finally(() => {
          setSyncing(false);
        });
    },
    [
      installationId,
      selectedDeviceIdRef,
      devices,
      fetchDevices,
      fetchDeviceStateReadOnly,
      invalidateDeviceStateCache,
      setInstallationSyncCache,
      setSyncing,
      setDevices,
      setDeviceStates,
      setSelectedDeviceId,
      setBulkDeviceLockdownStates,
      setCredentials,
      setActionHistory,
      showNotification,
      MAX_HISTORY_IN_MEMORY,
      shouldAllowFullSync,
      getStatusStore,
    ]
  );

  /**
   * Handler for doorNotification events
   *
   * Shows toast notification for door status changes.
   * Only shows for currently selected device.
   *
   * @param {Object} message - WebSocket message
   */
  const handleDoorNotification = useCallback(
    (message) => {
      const { deviceId, notificationType, message: doorMessage } = message;

      logger.log(
        `[doorNotification] 🚪 Door ${notificationType} for device ${deviceId}`
      );

      // Only show notification if this is the currently selected device
      if (deviceId !== selectedDeviceIdRef.current) {
        logger.log(
          `[doorNotification] Ignoring - device ${deviceId} is not the selected device (${selectedDeviceIdRef.current})`
        );
        return;
      }

      // Get device display name for the toast - use store.getState() to avoid stale closure
      const currentDevices = useDevicesStore.getState().devices;
      const device = currentDevices.find((d) => d.deviceId === deviceId);
      const deviceName = device?.displayName || "Device";

      logger.log(
        `[doorNotification] Found device: ${device?.displayName || "not found"}`
      );
      logger.log(`[doorNotification] Using device name: ${deviceName}`);

      // Show toast notification
      const toastMessage = `${deviceName}: ${doorMessage}`;
      showNotification({
        message: toastMessage,
        type: "info",
      });

      logger.log(`[doorNotification] Toast displayed: ${toastMessage}`);
    },
    [selectedDeviceIdRef, showNotification]
  );

  /**
   * Handler for subscribeConfirmation events
   *
   * Updates installation subscription status when SNS subscription is confirmed.
   *
   * @param {Object} message - WebSocket message
   */
  const handleSubscribeConfirmation = useCallback(
    (message) => {
      // Handle SNS subscription confirmation for events
      logger.log("[WebSocket] SubscribeConfirmation received:", message);

      if (
        message.installationId &&
        orgInstallContext?.updateInstallationSubscription
      ) {
        logger.log(
          `[WebSocket] Updating subscription status for installation ${message.installationId} to SUBSCRIBED`
        );
        orgInstallContext.updateInstallationSubscription(
          message.installationId,
          "SUBSCRIBED"
        );
      }
    },
    [orgInstallContext]
  );

  const handleLockNotification = useCallback(
    (message) => {
      const { deviceId, label, severity, message: lockMessage } = message;

      if (deviceId !== selectedDeviceIdRef.current) return;

      const currentDevices = useDevicesStore.getState().devices;
      const device = currentDevices.find((d) => d.deviceId === deviceId);
      const deviceName = device?.displayName || "Device";

      const notifType =
        severity === "error" ? "error" : severity === "warning" ? "warning" : "info";

      showNotification({
        message: `${deviceName}: ${lockMessage || label}`,
        type: notifType,
      });

      logger.log(`[lockNotification] ${severity} — ${deviceName}: ${label}`);
    },
    [selectedDeviceIdRef, showNotification]
  );

  return {
    handleStateRefreshed,
    handleDataRefreshed,
    handleDoorNotification,
    handleLockNotification,
    handleSubscribeConfirmation,
  };
};
